import React, { useState } from "react";
import {
  Container,
  Grid,
  Button,
  TextField,
  Typography,
  Paper,
  Box,
  Stack,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CallIcon from "@mui/icons-material/Call";
import EmailIcon from "@mui/icons-material/Email";
import LanguageIcon from "@mui/icons-material/Language";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import GitHubIcon from "@mui/icons-material/GitHub";
import DirectionsIcon from "@mui/icons-material/Directions";
import SendIcon from "@mui/icons-material/Send";
import emailjs from "emailjs-com";
import SectionHeading from "./SectionHeading";
import { brand } from "../theme";

// Official Google Maps embed for the Zealous head office (reliable, no API key).
const MAP_SRC =
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3907.3296799339414!2d78.13951807459888!3d11.671031488537412!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3babf04680edbe0f%3A0xcb56c889044c2542!2sZealous%20Academy%20of%20Career%20Training!5e0!3m2!1sen!2sin!4v1782976059163!5m2!1sen!2sin";
const MAP_LINK = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
  "Zealous Academy of Career Training, Salem, Tamil Nadu"
)}`;

const contactInfo = [
  {
    icon: <LocationOnIcon />,
    label: "Address",
    value: "#196/8, Gokulakrishnan Street, AVK Nagar, Opp. New Bus Stand, Salem – 636004, Tamil Nadu.",
  },
  { icon: <CallIcon />, label: "Phone", value: "+91 95973 34782", href: "tel:+919597334782" },
  { icon: <EmailIcon />, label: "Email", value: "reachus@zealoustechcorp.com", href: "mailto:reachus@zealoustechcorp.com" },
  { icon: <LanguageIcon />, label: "Website", value: "www.zealoustechcorp.com", href: "https://www.zealoustechcorp.com" },
];

const Contact = () => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
  const [errors, setErrors] = useState({ name: "", email: "", phone: "", message: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [sending, setSending] = useState(false);

  const validate = () => {
    let tempErrors = { name: "", email: "", phone: "", message: "" };
    let isValid = true;
    if (!formData.name) {
      tempErrors.name = "Name is required.";
      isValid = false;
    }
    if (!formData.email) {
      tempErrors.email = "Email is required.";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      tempErrors.email = "Email is not valid.";
      isValid = false;
    }
    if (!formData.phone) {
      tempErrors.phone = "Phone number is required.";
      isValid = false;
    } else if (!/^\d{10}$/.test(formData.phone)) {
      tempErrors.phone = "Phone number is not valid.";
      isValid = false;
    }
    if (!formData.message) {
      tempErrors.message = "Message is required.";
      isValid = false;
    }
    setErrors(tempErrors);
    return isValid;
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      setSending(true);
      emailjs
        .send("service_rh0xnn1", "template_q7ds9jm", formData, "eDMIe5oBkIwfMJj8G")
        .then(
          () => {
            setDialogMessage("Thank you! Your message has been sent successfully.");
            setDialogOpen(true);
            setSending(false);
          },
          (error) => {
            console.error("Error sending email:", error);
            setDialogMessage("Failed to send message. Please try again later.");
            setDialogOpen(true);
            setSending(false);
          }
        );
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    if (dialogMessage.startsWith("Thank you")) {
      setFormData({ name: "", email: "", phone: "", message: "" });
    }
  };

  // Inputs sit on a frosted card — keep them solid white for readability.
  const inputSx = {
    "& .MuiOutlinedInput-root": { bgcolor: "rgba(255,255,255,0.96)", borderRadius: 2 },
  };

  return (
    <section id="contacts-section" className="contacts-section" style={{ position: "relative" }}>
      {/* Full-width live map as the section background */}
      <Box aria-hidden sx={{ position: "absolute", inset: 0, zIndex: 0, overflow: "hidden" }}>
        <Box
          component="iframe"
          title="Zealous Tech Corp — Salem Head Office"
          src={MAP_SRC}
          loading="lazy"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          sx={{ border: 0, width: "100%", height: "100%", display: "block", filter: "grayscale(0.85) brightness(0.8)" }}
        />
        {/* Rich, layered brand scrim — deep blue base with soft glows for depth,
            keeping the map as a subtle texture behind the glass cards */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(900px 480px at 15% -10%, rgba(21,151,221,0.45), transparent 60%),
                         radial-gradient(720px 520px at 100% 120%, rgba(252,122,70,0.26), transparent 55%),
                         linear-gradient(135deg, rgba(9,38,61,0.90) 0%, rgba(11,62,99,0.76) 52%, rgba(9,38,61,0.92) 100%)`,
          }}
        />
      </Box>

      <Box sx={{ position: "relative", zIndex: 1, py: { xs: 7, md: 11 } }}>
        <Container>
          <SectionHeading
            eyebrow="GET IN TOUCH"
            title="Let's Build the Workforce of Tomorrow"
            subtitle="Reach our institutional partnership cells to schedule technical bootcamps, establish engineering internships, or sign formal academic MoUs."
            light
          />

          <Grid container spacing={{ xs: 3, md: 4 }} alignItems="stretch">
            {/* Info — translucent glass */}
            <Grid item xs={12} md={5}>
              <Paper
                elevation={0}
                sx={{
                  height: "100%",
                  p: { xs: 3, md: 4 },
                  color: "#fff",
                  bgcolor: "rgba(255,255,255,0.10)",
                  border: "1px solid rgba(255,255,255,0.22)",
                  backdropFilter: "blur(16px)",
                  WebkitBackdropFilter: "blur(16px)",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, fontSize: "1.15rem" }}>
                  Reach Us Directly
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.8)", mb: 2.5, fontSize: "0.9rem" }}>
                  Our head office in Salem, Tamil Nadu — drop by, call, or email us.
                </Typography>

                <Stack spacing={1.75} sx={{ flexGrow: 1 }}>
                  {contactInfo.map((info) => (
                    <Box key={info.label} sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                      <Box
                        sx={{
                          flexShrink: 0,
                          width: 42,
                          height: 42,
                          borderRadius: "12px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "rgba(255,255,255,0.14)",
                          color: "#fff",
                          "& svg": { fontSize: 20 },
                        }}
                      >
                        {info.icon}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.06em" }}>
                          {info.label.toUpperCase()}
                        </Typography>
                        {info.href ? (
                          <Typography
                            component="a"
                            href={info.href}
                            target={info.href.startsWith("http") ? "_blank" : undefined}
                            rel="noopener"
                            sx={{ color: "#fff", fontWeight: 600, fontSize: "0.92rem", wordBreak: "break-word", "&:hover": { textDecoration: "underline" } }}
                          >
                            {info.value}
                          </Typography>
                        ) : (
                          <Typography sx={{ color: "#fff", fontWeight: 500, fontSize: "0.92rem" }}>
                            {info.value}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 3 }}>
                  <Button
                    href={MAP_LINK}
                    target="_blank"
                    rel="noopener"
                    variant="contained"
                    color="secondary"
                    startIcon={<DirectionsIcon />}
                  >
                    Directions
                  </Button>
                  <IconButton
                    href="https://www.linkedin.com/in/zealous-academy-of-career-training-9927bb117/?originalSubdomain=in"
                    target="_blank"
                    rel="noopener"
                    aria-label="LinkedIn"
                    sx={{ color: "#fff", border: "1px solid rgba(255,255,255,0.3)", "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } }}
                  >
                    <LinkedInIcon />
                  </IconButton>
                  <IconButton
                    href="https://github.com/zealoustechcorp"
                    target="_blank"
                    rel="noopener"
                    aria-label="GitHub"
                    sx={{ color: "#fff", border: "1px solid rgba(255,255,255,0.3)", "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } }}
                  >
                    <GitHubIcon />
                  </IconButton>
                </Stack>
              </Paper>
            </Grid>

            {/* Form — frosted white glass card */}
            <Grid item xs={12} md={7}>
              <Paper
                elevation={0}
                sx={{
                  height: "100%",
                  p: { xs: 3, md: 4 },
                  bgcolor: "rgba(255,255,255,0.94)",
                  border: "1px solid rgba(255,255,255,0.6)",
                  backdropFilter: "blur(18px)",
                  WebkitBackdropFilter: "blur(18px)",
                  boxShadow: "0 30px 60px rgba(6,26,45,0.35)",
                }}
              >
                <Typography variant="h5" sx={{ color: brand.navy, fontWeight: 800, mb: 0.5 }}>
                  Send Us a Message
                </Typography>
                <Typography sx={{ color: brand.slate, mb: 3 }}>
                  Fill out the form and our partnership team will get back to you within one business day.
                </Typography>
                <form onSubmit={handleSubmit} noValidate>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Your Name" name="name" onChange={handleChange} value={formData.name} required error={!!errors.name} helperText={errors.name} sx={inputSx} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Phone" name="phone" type="tel" inputProps={{ inputMode: "numeric" }} onChange={handleChange} value={formData.phone} required error={!!errors.phone} helperText={errors.phone} sx={inputSx} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Email Address" name="email" type="email" onChange={handleChange} value={formData.email} required error={!!errors.email} helperText={errors.email} sx={inputSx} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Message" name="message" multiline rows={5} onChange={handleChange} value={formData.message} required error={!!errors.message} helperText={errors.message} sx={inputSx} />
                    </Grid>
                    <Grid item xs={12}>
                      <Button type="submit" fullWidth size="large" variant="contained" color="secondary" disabled={sending} endIcon={<SendIcon />} sx={{ py: 1.4 }}>
                        {sending ? "Sending..." : "Submit Enquiry"}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Contact Form</DialogTitle>
        <DialogContent>
          <DialogContentText>{dialogMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </section>
  );
};

export default Contact;
